import {Component, inject, input, output, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {InputText} from 'primeng/inputtext';
import {Message} from 'primeng/message';
import {TranslocoPipe} from '@jsverse/transloco';
import {of} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, switchMap} from 'rxjs/operators';
import {toObservable, takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {ProjectControllerService} from '@frontend/shared-api';
import {BaseDialogComponent} from '../../../../../../shared/components/dialog/base-dialog/base-dialog.component';

@Component({
  selector: 'app-project-copy-dialog',
  imports: [BaseDialogComponent, FormsModule, InputText, Message, TranslocoPipe],
  templateUrl: './project-copy-dialog.component.html',
})
export class ProjectCopyDialogComponent {
  readonly visible = input.required<boolean>();

  readonly visibleChange = output<boolean>();
  readonly confirmed = output<string>();

  protected readonly name = signal('');
  protected readonly nameTaken = signal(false);
  protected readonly checkingName = signal(false);

  private readonly projectControllerService = inject(ProjectControllerService);

  constructor() {
    toObservable(this.name)
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((name) => {
          const trimmed = name.trim();
          if (!trimmed) {
            this.checkingName.set(false);
            return of({exists: false});
          }
          this.checkingName.set(true);
          return this.projectControllerService
            .checkProjectNameExists({name: trimmed})
            .pipe(catchError(() => of({exists: false})));
        }),
        takeUntilDestroyed(),
      )
      .subscribe((result) => {
        this.checkingName.set(false);
        this.nameTaken.set(result.exists);
      });
  }

  protected get submitDisabled(): boolean {
    return !this.name().trim() || this.nameTaken() || this.checkingName();
  }

  protected onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.reset();
    }
    this.visibleChange.emit(visible);
  }

  protected onSubmit(): void {
    this.confirmed.emit(this.name().trim());
    this.reset();
    this.visibleChange.emit(false);
  }

  private reset(): void {
    this.name.set('');
    this.nameTaken.set(false);
    this.checkingName.set(false);
  }
}
